/**
 * FieldConfigPort: resolves config for any of 169 fields (AS3-faithful).
 *
 * WAVES_NUM and MONSTER_HP_RAW tables are taken verbatim from
 * `StageCollection.as` (_loc5_ and _loc6_). Monster HP is post-processed
 * with the same compression steps as AS3, then fed into our engine.
 *
 * Waypoints are generated procedurally per field (seeded by row*13+col).
 * Grid size and spawn pacing scale with wavesNum so each field feels
 * different but remains deterministic for a given field id.
 */

import type { FieldConfigPort } from "../../application/ports.js";
import type { FieldId, FieldConfig, Cell } from "../../domain/types.js";
import { activeCampaign, type CampaignConfig } from "../../campaign/index.js";
import { computeFullPath } from "../../domain/pathfinding.js";
import { applyOverride } from "../../domain/field-override.js";
import type { FieldOverrideStorage } from "../storage/field-override-adapter.js";
import { parseAuthoredField, authoredFieldToConfig } from "./field-yaml-loader.js";
import { monstersPerWaveForWaves, spawnIntervalMsForWaves } from "./field-tuning.js";

// --- AS3 raw tables (StageCollection.as, _loc5_ = wavesNum) ---

const WAVES_NUM: number[][] = [
  [50, 30, 40, 38, 30, 38, 38, 36, 30, 36, 38, 24, 50],
  [40, 40, 32, 38, 38, 44, 40, 36, 34, 34, 34, 32, 30],
  [30, 40, 38, 44, 46, 40, 42, 30, 34, 26, 34, 30, 32],
  [38, 42, 40, 60, 40, 42, 40, 36, 30, 50, 28, 30, 30],
  [36, 42, 40, 38, 40, 38, 40, 30, 40, 32, 30, 30, 30],
  [42, 38, 36, 40, 40, 40, 42, 40, 42, 40, 30, 30, 30],
  [44, 36, 18, 34, 40, 36, 80, 40, 42, 42, 30, 24, 28],
  [40, 18, 18, 16, 46, 42, 40, 40, 42, 18, 24, 28, 26],
  [16, 14, 18, 18, 14, 20, 16, 20, 20, 20, 24, 18, 28],
  [14, 14, 16, 30, 16, 20, 12, 16, 22, 40, 18, 28, 26],
  [16, 12, 14, 12, 12, 12, 8, 22, 22, 16, 26, 26, 28],
  [10, 14, 14, 10, 12, 10, 10, 10, 22, 24, 24, 26, 26],
  [50, 14, 14, 12, 10, 10, 8, 10, 12, 24, 24, 26, 50],
];

// --- AS3 raw tables (StageCollection.as, _loc6_ = monsterHpFirstWave, pre-transform) ---

const MONSTER_HP_RAW: number[][] = [
  [50, 53, 51, 50, 48, 48, 46, 40, 40, 38, 39, 36, 50],
  [52, 53, 52, 50, 49, 82, 47, 50, 40, 38, 37, 35, 30],
  [50, 56, 66, 66, 81, 82, 83, 44, 41, 37, 34, 34, 33],
  [50, 60, 65, 68, 80, 79, 84, 43, 36, 38, 32, 32, 33],
  [47, 58, 67, 70, 70, 78, 85, 44, 42, 31, 31, 30, 30],
  [50, 55, 68, 71, 76, 75, 66, 85, 94, 96, 28, 29, 29],
  [52, 54, 14, 72, 73, 74, 94, 92, 90, 84, 27, 27, 28],
  [54, 13, 13, 14, 87, 88, 89, 80, 81, 18, 25, 26, 26],
  [10, 11, 11, 12, 15, 14, 15, 16, 16, 19, 24, 25, 24],
  [9, 10, 10, 11, 12, 13, 15, 15, 17, 17, 18, 21, 24],
  [9, 8, 8, 7, 6, 6, 5, 13, 17, 18, 20, 21, 23],
  [14, 8, 7, 6, 6, 5, 4, 5, 17, 18, 16, 22, 22],
  [50, 8, 7, 7, 5, 4, 4, 4, 7, 18, 19, 22, 50],
];

/** AS3-faithful HP compression (_loc6_ post-loop). */
function transformHp(raw: number): number {
  let hp = raw;
  if (hp > 10) hp = Math.round(10 + 1 + 1.15 * (hp - 10));
  if (hp > 40) hp = Math.round(40 + 6 + 1.7 * (hp - 40));
  if (hp > 75) hp = Math.round(75 + 0.65 * (hp - 75));
  return hp;
}

// --- Engine tuning derived from wavesNum ---

function gridSizeForWaves(_waves: number): { rows: number; cols: number } {
  // AS3 canon: local field is 13×13. We previously scaled with wave count,
  // leaving early fields cramped (8×8 — barely room for a maze).
  return { rows: 13, cols: 13 };
}

/** Pause between waves (ms). Generous on starter fields — player needs breathing room to build. */
function interWaveDelayMsForWaves(waves: number): number {
  if (waves <= 10) return 3500;
  if (waves <= 14) return 2500;
  if (waves <= 20) return 2000;
  return 1500;
}

// --- Procedural field generator (seeded by idx) ---

/** Mulberry32 — tiny PRNG; deterministic per seed. */
function mulberry32(seed: number): () => number {
  let a = seed | 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pickInt(rng: () => number, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}

/**
 * Generate procedural field navigation: entryCells, exitCell, walkableCells.
 * Uses axis-aligned polyline turn-points to define the BFS path backbone,
 * then computes the full BFS path between them (walkableCells).
 * Replaces legacy generateWaypoints (Phase D).
 */
function generateProceduralField(
  seed: number,
  rows: number,
  cols: number,
  turnCount: number
): { entryCells: Cell[]; exitCell: Cell; walkableCells: Cell[] } {
  const rng = mulberry32(seed);

  // Entry on one of the edges (prefer bottom for AS3 feel).
  const entryEdge = pickInt(rng, 0, 3); // 0=bottom,1=top,2=left,3=right
  let cur: Cell;
  if (entryEdge === 0) cur = { row: rows - 1, col: pickInt(rng, 2, cols - 3) };
  else if (entryEdge === 1) cur = { row: 0, col: pickInt(rng, 2, cols - 3) };
  else if (entryEdge === 2) cur = { row: pickInt(rng, 2, rows - 3), col: 0 };
  else cur = { row: pickInt(rng, 2, rows - 3), col: cols - 1 };
  const entryCell: Cell = { ...cur };
  // Turn-points array: used to compute the full BFS path via waypoints.
  const turnPoints: Cell[] = [{ ...cur }];

  const innerMinRow = 1;
  const innerMaxRow = rows - 2;
  const innerMinCol = 1;
  const innerMaxCol = cols - 2;
  const MIN_JUMP = 4; // each segment ≥ 4 cells
  let horizontal = entryEdge === 2 || entryEdge === 3;
  for (let i = 0; i < turnCount; i++) {
    if (horizontal) {
      const lo = Math.max(innerMinCol, cur.col + MIN_JUMP);
      const hi = Math.min(innerMaxCol, cur.col - MIN_JUMP);
      const options: number[] = [];
      if (lo <= innerMaxCol) for (let v = lo; v <= innerMaxCol; v++) options.push(v);
      if (hi >= innerMinCol) for (let v = innerMinCol; v <= hi; v++) options.push(v);
      if (options.length > 0) {
        const col = options[pickInt(rng, 0, options.length - 1)];
        cur = { row: cur.row, col };
        turnPoints.push({ ...cur });
      }
    } else {
      const lo = Math.max(innerMinRow, cur.row + MIN_JUMP);
      const hi = Math.min(innerMaxRow, cur.row - MIN_JUMP);
      const options: number[] = [];
      if (lo <= innerMaxRow) for (let v = lo; v <= innerMaxRow; v++) options.push(v);
      if (hi >= innerMinRow) for (let v = innerMinRow; v <= hi; v++) options.push(v);
      if (options.length > 0) {
        const row = options[pickInt(rng, 0, options.length - 1)];
        cur = { row, col: cur.col };
        turnPoints.push({ ...cur });
      }
    }
    horizontal = !horizontal;
  }

  // Exit on an edge, orthogonal to the last move; choose a different edge.
  const exitEdges = [0, 1, 2, 3].filter((e) => e !== entryEdge);
  const exitEdge = exitEdges[pickInt(rng, 0, exitEdges.length - 1)];
  if (exitEdge === 0) cur = { row: rows - 1, col: cur.col };
  else if (exitEdge === 1) cur = { row: 0, col: cur.col };
  else if (exitEdge === 2) cur = { row: cur.row, col: 0 };
  else cur = { row: cur.row, col: cols - 1 };
  const last = turnPoints[turnPoints.length - 1];
  if (cur.row !== last.row || cur.col !== last.col) turnPoints.push({ ...cur });

  // Ensure minimum 2 turn-points. Fallback if RNG collapsed to single point.
  if (turnPoints.length < 2) {
    turnPoints.push({ row: (turnPoints[0].row + 1) % rows, col: (turnPoints[0].col + 1) % cols });
  }

  const exitCellResult: Cell = { ...turnPoints[turnPoints.length - 1] };

  // Compute walkableCells: union of BFS segments between consecutive turn-points.
  // This preserves the maze-like detour through all turns instead of a direct short path.
  const seen = new Set<string>();
  const walkableCells: Cell[] = [];
  for (let i = 0; i + 1 < turnPoints.length; i++) {
    const seg = computeFullPath(rows, cols, [turnPoints[i], turnPoints[i + 1]], new Set<string>());
    for (const cell of seg) {
      const k = `${cell.row},${cell.col}`;
      if (!seen.has(k)) {
        seen.add(k);
        walkableCells.push(cell);
      }
    }
  }

  return {
    entryCells: [entryCell],
    exitCell: exitCellResult,
    walkableCells: walkableCells.length > 0 ? walkableCells : [entryCell, exitCellResult],
  };
}

/** Stable per-field seed mixing coords + waves so each field gets a unique path. */
function fieldSeed(row: number, col: number, waves: number): number {
  return ((row * 131 + col * 17 + waves * 3) | 0) >>> 0;
}

// --- Blocked cell generator ---

/**
 * Generates obstacle cells for a field, deterministically seeded.
 * Guarantees:
 *   - No obstacle overlaps a walkable path cell or any explicitly protected cell.
 *   - A 1-cell buffer around the initial BFS path is left free.
 *   - After placement, BFS from entry→exit still yields a path (rollback if not).
 * @param targetRatio fraction of eligible cells to block (default 0.2)
 * @param extraProtected additional cells guaranteed never blocked (prebuilt buildings)
 */
function generateBlockedCells(
  seed: number,
  rows: number,
  cols: number,
  entryCells: Cell[],
  exitCell: Cell,
  walkableCells: Cell[],
  targetRatio = 0.2,
  extraProtected: Cell[] = [],
): Cell[] {
  if (walkableCells.length === 0) return [];
  const rng = mulberry32(seed ^ 0xdeadbeef);

  // 1. Build a "protected" set: walkable path cells + their 4-connected neighbours.
  const protected_ = new Set<string>();
  for (const cell of walkableCells) {
    protected_.add(`${cell.row},${cell.col}`);
    for (const [dr, dc] of [[-1,0],[1,0],[0,-1],[0,1]] as const) {
      const nr = cell.row + dr;
      const nc = cell.col + dc;
      if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
        protected_.add(`${nr},${nc}`);
      }
    }
  }

  // Also protect all entry cells and exit cell explicitly.
  for (const entry of entryCells) {
    protected_.add(`${entry.row},${entry.col}`);
  }
  protected_.add(`${exitCell.row},${exitCell.col}`);
  // Protect extra cells (e.g. prebuilt building positions).
  for (const c of extraProtected) {
    protected_.add(`${c.row},${c.col}`);
  }

  // 2. Collect candidate cells.
  const candidates: Cell[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (!protected_.has(`${r},${c}`)) {
        candidates.push({ row: r, col: c });
      }
    }
  }

  // 3. Fisher-Yates shuffle using rng.
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = candidates[i];
    candidates[i] = candidates[j];
    candidates[j] = tmp;
  }

  // 4. Take targetRatio of candidates.
  const target = Math.floor(candidates.length * targetRatio);
  const chosen = candidates.slice(0, target);

  // 5. BFS verification using walkableCells (bfsInAllowed).
  // Entry/exit are always passable by design, so just verify connectivity.
  const blockedSet = new Set<string>(chosen.map(c => `${c.row},${c.col}`));
  const allowedSet = new Set<string>(walkableCells.map(c => `${c.row},${c.col}`));

  // BFS reachability check: traverses walkableCells minus blockedSet.
  const bfsCheck = (s: Cell, e: Cell): boolean => {
    const key = (r: number, c: number) => `${r},${c}`;
    const sk = key(s.row, s.col);
    const ek = key(e.row, e.col);
    if (sk === ek) return true;
    const visited = new Set<string>();
    const queue = [sk];
    visited.add(sk);
    const dirs = [[-1,0],[1,0],[0,-1],[0,1]] as const;
    while (queue.length > 0) {
      const cur = queue.shift()!;
      if (cur === ek) return true;
      const [cr, cc] = cur.split(",").map(Number);
      for (const [dr, dc] of dirs) {
        const nr = cr+dr, nc = cc+dc;
        const nk = key(nr, nc);
        if (visited.has(nk)) continue;
        const isStartOrEnd = nk === sk || nk === ek;
        if (!isStartOrEnd && !allowedSet.has(nk)) continue;
        if (!isStartOrEnd && blockedSet.has(nk)) continue;
        visited.add(nk);
        queue.push(nk);
      }
    }
    return false;
  };

  // Check all entries can still reach exit.
  let allReachable = true;
  for (const entry of entryCells) {
    if (!bfsCheck(entry, exitCell)) { allReachable = false; break; }
  }

  if (allReachable) return chosen;

  // 6. Rollback: remove obstacle cells one by one until path is restored.
  for (let i = chosen.length - 1; i >= 0; i--) {
    blockedSet.delete(`${chosen[i].row},${chosen[i].col}`);
    let ok = true;
    for (const entry of entryCells) {
      if (!bfsCheck(entry, exitCell)) { ok = false; break; }
    }
    if (ok) return chosen.slice(0, i);
  }

  return [];
}

// --- Public API ---

function buildFieldConfig(fieldId: FieldId, campaign: CampaignConfig): FieldConfig | null {
  // 1. Authored YAML takes priority over procedural generation.
  const authoredField = campaign.map.authoredFields.get(fieldId);
  if (authoredField) {
    // Errors from authored YAML are intentionally not swallowed:
    // a broken YAML fails the field loudly rather than silently falling back.
    const authored = parseAuthoredField(authoredField.yamlText, authoredField.fileName);
    return authoredFieldToConfig(authored);
  }

  const coords = campaign.map.fieldIdToCoords(fieldId);
  if (!coords) return null;
  const { row, col } = coords;
  const waves = WAVES_NUM[row][col];
  const hp = transformHp(MONSTER_HP_RAW[row][col]);

  const { rows, cols } = gridSizeForWaves(waves);
  const monstersPerWave = monstersPerWaveForWaves(waves);
  const spawnIntervalMs = spawnIntervalMsForWaves(waves);

  // Turn count scales with waves; minimum 5 so every path covers enough grid
  // for towers to engage multiple times (short paths collapsed early-game).
  const turnCount = Math.max(5, Math.min(8, Math.round(4 + waves / 10)));
  const seed = fieldSeed(row, col, waves);
  const { entryCells, exitCell, walkableCells } = generateProceduralField(seed, rows, cols, turnCount);
  const blockedCells = generateBlockedCells(seed, rows, cols, entryCells, exitCell, walkableCells);

  // Armor growth stays at AS3's unified 0.11; higher grows per-wave via growth rate.
  const interWaveDelayMs = interWaveDelayMsForWaves(waves);
  // Starter fields get extra prep time before wave 1 so the player can set up.
  const firstWaveDelayMs = waves <= 10 ? 4000 : waves <= 14 ? 2500 : 1500;
  return {
    rows,
    cols,
    entryCells,
    exitCell,
    walkableCells,
    blockedCells,
    wavesNum: waves,
    monstersPerWave,
    spawnIntervalMs,
    monsterBaseHp: hp,
    armorGrowthRate: 0.11,
    firstWaveDelayMs,
    interWaveDelayMs,
  };
}

export function createFieldConfigAdapter(
  overrideStorage: FieldOverrideStorage,
  campaign: CampaignConfig = activeCampaign
): FieldConfigPort {
  /** Lazily cached to avoid re-seeding waypoints on repeated entries to the same field. */
  const cache = new Map<FieldId, FieldConfig | null>();
  return {
    getFieldConfig(fieldId: FieldId): FieldConfig | null {
      if (!cache.has(fieldId)) cache.set(fieldId, buildFieldConfig(fieldId, campaign));
      const base = cache.get(fieldId) ?? null;
      if (!base) return null;
      const override = overrideStorage.getOverride(fieldId);
      if (!override) return base;
      return applyOverride(base, override);
    },
    invalidate(fieldId: FieldId): void {
      cache.delete(fieldId);
    },
  };
}
